import "./Brand.scss";
import { useEffect, useState } from "react";
import BrandApi from "../../api/BrandApi";
import { ColorDto, FontDto, LogoDto, UploadedFontDto } from "../../domain/BrandDataDto";
import { Button, message, Popover, Skeleton, Upload } from "antd";
import { ConfirmDeleteLogoModal } from "../brand/ConfirmDeleteLogoModal";
import { ConfirmDeleteColorModal } from "../brand/ConfirmDeleteColorModal";
import { ConfirmDeleteUploadedFontModal } from "../brand/ConfirmDeleteUploadedFontModal";
import { TimberFont, LocationDto, MemberDtoPlanEnum } from "../../generated/dashboard-client";
import BrandFont, { BrandFontType } from "../brand/BrandFont";
import DashboardApi from "../../api/DashboardApi";
import { DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import ColorPicker from "../brand/ColorPicker";
import { useRecoilState } from "recoil";
import { locationsState, memberState } from "../../state/atoms";
import { ProStar } from "../account/misc/ProStar";
import { ConfirmFontUploadModal } from "../brand/ConfirmFontUploadModal";
import { tick } from "../../util/utils";
import SearchCategories from "../SearchCategories";
import { CopyFromLocationModal } from "../brand/CopyFromLocationModal";
import { AltTextModal } from "../brand/AltTextModal";
import LogoCard from "../brand/LogoCard";

const AcceptedFileTypes = ["image/jpeg", "image/png", "image/gif", "image/svg+xml"];
const AcceptedFontFileTypes = [".ttf", ".otf", ".woff"];

export interface BrandProps {
  activeLocation: LocationDto;
}

export default function Brand({ activeLocation }: BrandProps) {
  const [member] = useRecoilState(memberState);
  const [locations] = useRecoilState(locationsState);

  const [fonts, setFonts] = useState([] as TimberFont[]);

  const [isConfirmDeleteColorModal, setConfirmDeleteColorModal] = useState(false);
  const [isConfirmDeleteLogoModal, setConfirmDeleteLogoModal] = useState(false);
  const [isConfirmDeleteUploadedFontModal, setConfirmDeleteUploadedFontModal] = useState(false);
  const [currentColor, setCurrentColor] = useState({} as ColorDto);
  const [currentLogo, setCurrentLogo] = useState({} as LogoDto);
  const [currentUploadedFont, setCurrentUploadedFont] = useState({} as UploadedFontDto);

  const [isLogoUploading, setLogoUploading] = useState(false);
  const [isBrandCopying, setBrandCopying] = useState(false);
  const [isLogoDeleting, setLogoDeleting] = useState(false);
  const [isUploadedFontDeleting, setUploadedFontDeleting] = useState(false);

  const [isConfirmFontUploadModal, setConfirmFontUploadModal] = useState(false);
  const [currentFontFile, setCurrentFontFile] = useState({} as File);
  const [isFontUploading, setFontUploading] = useState(false);
  const [isCopyFromLocationModal, setCopyFromLocationModal] = useState(false);
  const [isAltTextModal, setAltTextModal] = useState(false);

  const [id, setId] = useState("");
  const [loading, setLoading] = useState(false);
  const [colors, setColors] = useState([] as ColorDto[]);
  const [logos, setLogos] = useState([] as LogoDto[]);
  const [header, setHeader] = useState({} as FontDto);
  const [subHeader, setSubHeader] = useState({} as FontDto);
  const [bodyText, setBodyText] = useState({} as FontDto);
  const [uploadedFonts, setUploadedFonts] = useState([] as UploadedFontDto[]);

  const [currentPickerColor, setCurrentPickerColor] = useState("#ffffff");
  const [isColorPickerVisible, setIsColorPickerVisible] = useState(false);

  const [prevLocation, setPrevLocation] = useState({} as LocationDto);

  const memberId = member.internalId;
  const name = activeLocation.name;

  useEffect(() => {
    setLoading(true);
    DashboardApi.getFonts().then((resp) => {
      setFonts(resp.data);
    });
  }, []);

  useEffect(() => {
    if (!activeLocation || !activeLocation.internalId) return;

    setPrevLocation(activeLocation);

    if (activeLocation.id !== prevLocation.id) {
      setLoading(true);
      BrandApi.getBrand(activeLocation.internalId)
        .then((resp) => {
          const { id, colors, fontHeader, fontSubheader, fontBodyText, logos, uploadedFonts } =
            resp.data;
          setId(id);
          setColors(colors);
          setHeader(fontHeader);
          setSubHeader(fontSubheader);
          setBodyText(fontBodyText);
          setLogos(logos);
          setUploadedFonts(uploadedFonts.sort((a, b) => b.createdDate - a.createdDate));
          setLoading(false);

          try {
            uploadedFonts.forEach(function (uploadedFont) {
              const url = `url('https://mhm-timber.s3.amazonaws.com/${uploadedFont.path.replace(/ /g, '%20')}')`;
              const font = new FontFace(uploadedFont.name, url);
              font.load();
              document.fonts.add(font);
            });
          } catch (err: any) {
            console.log(err.stack);
          }
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [activeLocation, prevLocation.id]);

  function save(
    logos: LogoDto[],
    colors: ColorDto[],
    fontHeader: FontDto,
    fontSubheader: FontDto,
    fontBodyText: FontDto
  ) {
    BrandApi.saveBrand({
      name,
      id,
      memberId: memberId.toString(),
      logos,
      colors,
      fontHeader,
      fontBodyText,
      fontSubheader,
    }).then(resp => {
      console.log("Saved brand info");
    }).catch((err) => {
      console.log("Save failed: " + err);
    });
  }

  function quickSave() {
    tick(() => {
      save(logos, colors, header, subHeader, bodyText);
    });
  }

  function onColorSave(color: string) {
    let isDupeColor = false;
    colors.forEach(obj => {
      if (obj.colorCode === color) {
        isDupeColor = true;
      }
    })
    if (!isDupeColor) {
      const updated = [{ colorCode: color }, ...colors];
      setColors(updated);
      save(logos, updated, header, subHeader, bodyText);
    }
  }

  function removeColor(color: ColorDto) {
    const updated = colors.filter((it) => it.colorCode !== color.colorCode);
    setColors(updated);
    save(logos, updated, header, subHeader, bodyText);
    setConfirmDeleteColorModal(false);
  }

  function removeLogo(logo: LogoDto) {
    setLogoDeleting(true);
    BrandApi.deleteLogo(logo.id).then(() => {
      setLogos(logos.filter((it) => it.id !== logo.id));
      setLogoDeleting(false);
      setConfirmDeleteLogoModal(false);
    });
  }

  function removeUploadedFont(font: UploadedFontDto) {
    setUploadedFontDeleting(true);
    BrandApi.deleteFont(font.id).then(() => {
      setUploadedFonts(uploadedFonts.filter((it) => it.id !== font.id));
      setUploadedFontDeleting(false);
      setConfirmDeleteUploadedFontModal(false);
    });
  }

  function onUploadChange(file: File) {
    if (AcceptedFileTypes.indexOf(file.type?.toLowerCase() ?? "") === -1) {
      message.error("Supported file types are .svg, .png, .jpg/.jpeg, and .gif.");
      return;
    }

    if ((file.size ?? 0) > 5 * 1024 * 1025) {
      message.error(
        "This image can't be uploaded because it exceeds the 5 MB file size limit."
      );
      return;
    }

    var newFile = new File([file], file.name.replace(/ /g, "_"), {
      type: file.type,
      lastModified: file.lastModified
    });

    setLogoUploading(true);

    const formData = new FormData();
    formData.append("file", newFile);

    BrandApi.uploadLogo(id, formData).then((resp) => {
      setLogos([resp.data, ...logos]);
      setLogoUploading(false);
    }).catch(() => {
      setLogoUploading(false);
      message.error("Error uploading logo");
    });
  }

  function onCustomFontChange(file: File) {
    let acceptedFile = false;
    const fileName = file.name.toLowerCase();
    AcceptedFontFileTypes.forEach((type) => {
      if (fileName.endsWith(type)) {
        acceptedFile = true;
      }
    });
    if (!acceptedFile) {
      message.error("Supported file types are .ttf, .otf, and .woff.");
      return;
    }

    if ((file.size ?? 0) > 5 * 1024 * 1025) {
      message.error(
        "This font can't be uploaded because it exceeds the 5 MB file size limit."
      );
      return;
    }

    setCurrentFontFile(file);
    setConfirmFontUploadModal(true);
  }

  function uploadFont(file: File) {
    var newFile = new File([file], file.name.replace(/ /g, "_"), {
      type: file.type,
      lastModified: file.lastModified
    });
    const formData = new FormData();
    formData.append("file", newFile);
    setFontUploading(true);

    BrandApi.uploadFont(id, formData).then((resp) => {
      let newFont = resp.data;
      const font = new FontFace(newFont.name, `url('https://mhm-timber.s3.amazonaws.com/${newFont.path.replace(/ /g, '%20')}')`);
      font.load();
      document.fonts.add(font);
      setUploadedFonts([newFont, ...uploadedFonts]);
      setConfirmFontUploadModal(false);
      setFontUploading(false);
    }).catch(() => {
      setConfirmFontUploadModal(false);
      setFontUploading(false);
    });
  }

  function deleteUploadedFont(font: UploadedFontDto) {
    setConfirmDeleteUploadedFontModal(true);
    setCurrentUploadedFont(font);
  }

  function deleteBrandColor(color: ColorDto) {
    setConfirmDeleteColorModal(true);
    setCurrentColor(color);
  }

  function deleteBrandLogo(logo: LogoDto) {
    setConfirmDeleteLogoModal(true);
    setCurrentLogo(logo);
  }

  function editAltText(logo: LogoDto) {
    setAltTextModal(true);
    setCurrentLogo(logo);
  }

  function handleColorPickerVisible() {
    if (isColorPickerVisible) {
      onColorSave(currentPickerColor);
    }
    setIsColorPickerVisible(!isColorPickerVisible);
  }

  function goPro() {
    window.MHM.ProModal.upgrade({ memberId: member.id, onSuccess: () => window.location.reload() });
  }

  function nameWithoutExtension(name: String) {
    let fileExtPos = name.lastIndexOf(".");
    if (fileExtPos >= 0) {
      return name.substring(0, fileExtPos);
    } else {
      return name;
    }
  }

  function copyFromLocation(locId: number) {
    setBrandCopying(true);
    BrandApi.copyFromLocation(id, locId).then(resp => {
      setBrandCopying(false);
      const { colors, fontHeader, fontSubheader, fontBodyText, logos, uploadedFonts } =
        resp.data;
      setColors(colors);
      setHeader(fontHeader);
      setSubHeader(fontSubheader);
      setBodyText(fontBodyText);
      setLogos(logos);
      setUploadedFonts(uploadedFonts);
      setCopyFromLocationModal(false);

      try {
        uploadedFonts.forEach(function (uploadedFont) {
          const url = `url('https://mhm-timber.s3.amazonaws.com/${uploadedFont.path.replace(/ /g, '%20')}')`;
          const font = new FontFace(uploadedFont.name, url);
          font.load();
          document.fonts.add(font);
        });
      } catch (err: any) {
        console.log(err.stack);
      }
    })
  }

  return (
    <div className={"brand page"}>
      <div className="mobile-search-cats">
        <SearchCategories />
      </div>
      {loading && <Skeleton active />}

      {!loading && (
        <>
          <h3>
            My Brand
            {locations.length > 1 && <Button type={'default'} onClick={() => setCopyFromLocationModal(true)}>Copy</Button>}
          </h3>

          <section className={"logos"}>
            <h3>Logos</h3>

            <ul>
              <li className={"add"}>
                {!isLogoUploading &&
                  <Upload showUploadList={false} beforeUpload={onUploadChange}>
                    +
                  </Upload>}
                {isLogoUploading &&
                  <div className={"loading"}>
                    <LoadingOutlined />
                  </div>}
              </li>
              {logos.map((it) => (
                <LogoCard 
                  logo={it}
                  editAltText={editAltText}
                  deleteLogo={deleteBrandLogo}
                />
              ))}
            </ul>
          </section>

          <section className={"colors"}>
            <h3>Colors</h3>

            <ul>
              <li className={"add"}>
                <Popover
                  placement="bottomLeft"
                  trigger="click"
                  onVisibleChange={handleColorPickerVisible}
                  getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                  content={<ColorPicker onColorChange={setCurrentPickerColor} />}>
                  <Button>+</Button>
                </Popover>
              </li>
              {colors.map((it) => (
                <li key={it.colorCode}>
                  <Button
                    className={"rmv"}
                    type={"link"}
                    onClick={() => deleteBrandColor(it)}
                  >
                    <DeleteOutlined />
                  </Button>
                  <div
                    className={"clr"}
                    style={{ backgroundColor: it.colorCode }}
                  />
                </li>
              ))}
            </ul>
          </section>

          <section className={"fonts"}>
            <h3>Brand Fonts</h3>
            <ul>
              <li>
                <BrandFont
                  type={BrandFontType.Header}
                  label={"Header"}
                  font={header}
                  fonts={fonts}
                  uploadedFonts={uploadedFonts}
                  onChange={(font) => {
                    setHeader(font);
                    quickSave();
                  }}
                />
              </li>
              <li>
                <BrandFont
                  type={BrandFontType.SubHeader}
                  label={"Subheader"}
                  font={subHeader}
                  fonts={fonts}
                  uploadedFonts={uploadedFonts}
                  onChange={(font) => {
                    setSubHeader(font);
                    quickSave();
                  }}
                />
              </li>
              <li>
                <BrandFont
                  type={BrandFontType.BodyText}
                  label={"Body text"}
                  font={bodyText}
                  fonts={fonts}
                  uploadedFonts={uploadedFonts}
                  onChange={(font) => {
                    setBodyText(font);
                    quickSave();
                  }}
                />
              </li>
            </ul>
          </section>

          <section className={"uploaded-fonts"}>
            <h3>Uploaded Fonts{member.plan === "FREE" && <ProStar />}</h3>

            <ul>
              {uploadedFonts.map((it) => (
                <li key={it.id}>
                  <div className={"uploaded-font"} style={{ fontFamily: `'${it.name}'` }}>
                    {nameWithoutExtension(it.name)}
                  </div>
                  <Button
                    className={"rmv"}
                    type={"link"}
                    onClick={() => deleteUploadedFont(it)}
                  >
                    <DeleteOutlined />
                  </Button>
                </li>
              ))}

              <li className={"add"}>
                {member.plan === MemberDtoPlanEnum.Free &&
                  <Button onClick={goPro}>+</Button>}
                {member.plan !== MemberDtoPlanEnum.Free &&
                  <Upload
                    accept={".ttf, .otf, .woff"}
                    showUploadList={false}
                    beforeUpload={onCustomFontChange}
                  >
                    +
                  </Upload>}
              </li>
            </ul>
          </section>
        </>
      )}

      <ConfirmDeleteColorModal
        isVisible={isConfirmDeleteColorModal}
        onCancel={() => setConfirmDeleteColorModal(false)}
        onConfirm={() => removeColor(currentColor)}
      />

      <ConfirmDeleteLogoModal
        isVisible={isConfirmDeleteLogoModal}
        onCancel={() => setConfirmDeleteLogoModal(false)}
        onConfirm={() => removeLogo(currentLogo)}
        isLoading={isLogoDeleting}
      />

      <ConfirmDeleteUploadedFontModal
        isVisible={isConfirmDeleteUploadedFontModal}
        onCancel={() => setConfirmDeleteUploadedFontModal(false)}
        onConfirm={() => removeUploadedFont(currentUploadedFont)}
        isLoading={isUploadedFontDeleting}
      />

      <ConfirmFontUploadModal
        isVisible={isConfirmFontUploadModal}
        onCancel={() => setConfirmFontUploadModal(false)}
        onConfirm={() => uploadFont(currentFontFile)}
        isLoading={isFontUploading}
      />

      <CopyFromLocationModal
        isVisible={isCopyFromLocationModal}
        onCancel={() => setCopyFromLocationModal(false)}
        onConfirm={copyFromLocation}
        isLoading={isBrandCopying}
        activeLocation={activeLocation}
      />

      <AltTextModal
        open={isAltTextModal}
        onClose={() => setAltTextModal(false)}
        selectedLogo={currentLogo}
        logos={logos}
        setLogos={setLogos}
      />
    </div>
  );
}
